import React, { useState } from 'react';
import { Label } from '../../../../../../components/Form/Label/Label';
import { Grid } from '../../../../../../components/Grid/Grid';
import { AsyncSearch } from '../../../../../../components/Form/AsyncSearch/AsyncSearch';
import { t } from '../../../../../../locales/translate';
import { AddEventItem, addItemToOrder } from '../../../../../../model/item';
import { Select } from '../../../../../../components/Form/Select/Select';
import { Event, getEventDetails, PricingCategory, EventProductVariant } from '../../../../../../model/event';
import { loadPricingCategories } from '../../../../../../model/price';
import { loadSeatStatus } from '../../../../../../model/contingent';
import { handleError, handleSuccess } from '../../../../../../model/notification';
import { VenueSection } from '../../../../../../model/venue';
import { Spinner } from '../../../../../../components/Spinner/Spinner';
import { useChanges, Form } from '../../../../../../components/Form/Form';
import { getDocumentFieldOptions } from '../../../../../../model/document';
import { Order } from '../../../../../../model/order';
import { writeDB } from '../../../../../../model/store';
import { Address } from '../../../../../../model/company';
import { deleteDelivery } from '../../../../../../model/delivery';
import { deleteInvoice } from '../../../../../../model/invoice';
import { AddItemsFormFooter, createDocuments, checkDocumentOption } from '../AddItemsForm';
import { ScrollWrapper } from '../../../../../../components/ScrollWrapper/ScrollWrapper';
import { Stack } from '../../../../../../components/Stack/Stack';
import { Spacer } from '../../../../../../components/Spacer/Spacer';
import { useRemoteData } from '../../../../../../model/data';
import { getAllShops } from '../../../../../../model/shop';
import { orderBy, uniq } from 'lodash';
import { EventVariants } from './EventVariants';
import { TicketsSelect } from './TicketsSelect';
import EventPools from './EventPools';
import { useReloadKey } from '../../../../../../util/util';

interface Props {
  order: Order;
  preferedAddresses: {
    delivery: Address;
    invoice: Address;
  };
  onClose: () => void;
  onChange: () => void;
}

function displayDeliverySelect(model: AddEventItem): boolean {
  const selectedVariant = model.selectedVariants?.find((item) => item.id === model.variantId);

  if (selectedVariant?.physical) {
    return true;
  }

  return false;
}

function canSubmit(model: AddEventItem): boolean {
  // Check if quantity is more than 0
  const amount = (model.count && model.count > 0) || (model.seatIds && model.seatIds.length > 0);
  // Check if the event section is selected
  const eventSectionSelected = !!model.sectionId;
  // Check if every ticket has a category selected
  const ticketTypeSelected = !!model.variantId;
  // Check if both documents are selected
  const documentSelected = !!(model.deliveryId !== 'no' || model.invoiceId !== 'no');
  // Check if shop is selected
  const selectedShop = model.shopId;

  return !!amount && eventSectionSelected && ticketTypeSelected && documentSelected && !!selectedShop;
}

function getProductVariants(
  pricingCategories: PricingCategory[],
  section: VenueSection
): EventProductVariant[] | undefined {
  const pricingCategoryIds = section.generalAdmission
    ? section.pricingCategoryId
      ? [section.pricingCategoryId]
      : []
    : (uniq(section?.seats?.map((e) => e.pricingCategoryId)) ?? []);

  const categories = pricingCategories.filter((item) => pricingCategoryIds.includes(item.id));

  return categories.flatMap((category) =>
    category.productVariants.map((item) => ({
      ...item,
      pricingCategoryId: category.id,
      categoryName: category.name,
    }))
  );
}

export function EventItemsForm({ order, preferedAddresses, onClose, onChange }: Props): JSX.Element {
  const [, setChanges, model] = useChanges<AddEventItem>({ shopId: order.shopId });
  const [loading, setLoading] = useState(false);
  const [loadingEventData, setLoadingEventData] = useState(false);
  const [createAnother, setCreateAnother] = useState(false);
  const [shops, shopsLoading] = useRemoteData(
    () => getAllShops(),
    (db) => db.table('shops').all
  );
  const [poolsKey, reloadPools] = useReloadKey();
  const event = model.event;

  // Delivery Options
  const deliveryOptions = getDocumentFieldOptions(order.deliveries, 'delivery');
  const invoiceOptions = getDocumentFieldOptions(order.invoices, 'invoice');

  async function getEventData(event: Event): Promise<void> {
    setLoadingEventData(true);
    try {
      await Promise.all([getEventDetails({ eventId: event.id }), loadSeatStatus(event)]);
      await loadPricingCategories({ event });
    } catch (e) {
      handleError(e);
    }
    setLoadingEventData(false);
  }

  async function setEvent(event?: Event): Promise<void> {
    // clear model
    if (!event) {
      const { deliveryId, invoiceId } = model;

      return setChanges({
        deliveryId,
        invoiceId,
      });
    }
    // load data for the event
    await getEventData(event);
    const eventWithData = writeDB.table('events').find(event.id);

    // save changes
    setChanges({
      ...model,
      pool: undefined,
      event: eventWithData,
      count: 1,
      deliveryId: model.deliveryId ?? deliveryOptions[0].value,
      invoiceId: model.invoiceId ?? invoiceOptions[0].value,
    });
  }

  function handleOnInput(newModel: AddEventItem, field: string): void {
    if (field === 'variantId') {
      const eventVariant = model.selectedVariants?.find((item) => item.id === newModel.variantId);

      if (eventVariant && !eventVariant.physical) {
        newModel.deliveryId = 'no';
      } else {
        newModel.deliveryId = model.deliveryId ?? deliveryOptions[0].value;
      }
    }

    if (field === 'sectionId') {
      newModel.seatIds = [];
      newModel.variantId = undefined;

      const selectedSection = event?.venuePlan?.sections.find((item) => item.id === newModel.sectionId);

      const selectedVariants =
        event?.pricingCategories && selectedSection
          ? getProductVariants(event?.pricingCategories, selectedSection)
          : [];

      return setChanges({
        ...newModel,
        pool: undefined,
        selectedSection,
        selectedVariants,
      });
    }

    setChanges(newModel);
  }

  async function handleSubmit(): Promise<void> {
    setLoading(true);

    const [createdDelivery, createdInvoice] = await createDocuments({
      orderId: order.id,
      delivery: model.deliveryId === 'new',
      invoice: model.invoiceId === 'new',
      preferedAddresses,
    });

    const deliveryId = createdDelivery?.id || checkDocumentOption(model.deliveryId);
    const invoiceId = createdInvoice?.id || checkDocumentOption(model.invoiceId);

    try {
      await addItemToOrder(order.id, {
        deliveryId,
        invoiceId,
        productVariantId: model.variantId ?? '',
        sectionId: model.sectionId,
        count: Number(model.count),
        pool: model.pool,
        seatIds: model.selectedSection?.generalAdmission ? undefined : model.seatIds,
        shopId: model.shopId,
      });
      if (!createAnother) {
        onClose();
      } else {
        handleSuccess(t.success.addedItems(t.model.item._name));
        setChanges({
          ...model,
          deliveryId: deliveryId ?? model.deliveryId ?? deliveryOptions[0].value,
          invoiceId: invoiceId ?? model.invoiceId ?? invoiceOptions[0].value,
        });
        reloadPools();
      }
      onChange();
    } catch (e) {
      handleError(e);
      // If add item fails we need to clear the documents:
      if (createdDelivery) {
        deleteDelivery(order.id, createdDelivery.id);
      }
      if (createdInvoice) {
        deleteInvoice(order.id, createdInvoice.id);
      }
    }
    setLoading(false);
  }

  return (
    <>
      <ScrollWrapper className="add-items-modal__content">
        <Form model={model} onInput={handleOnInput}>
          <Stack>
            {/* Select EVent */}
            <AsyncSearch
              label={`${t.model.event._name}`}
              name="productId"
              onSelect={(item) => setEvent(item as Event)}
              searchIn={['event']}
              maxResults={20}
              required
            />
            {loadingEventData ? (
              <Spinner color="red" text="Loading event details" />
            ) : (
              <>
                {event && (
                  <>
                    <EventVariants
                      modelEvent={event}
                      onLoad={(newModel) => {
                        setChanges({
                          ...model,
                          ...newModel,
                          selectedVariants: newModel.selectedSection
                            ? getProductVariants(event.pricingCategories, newModel.selectedSection)
                            : undefined,
                        });
                      }}
                    />
                    {model.selectedSection && <TicketsSelect model={model} selectedSection={model.selectedSection} />}
                    {model.sectionId && !model.selectedSection?.seats.length && (
                      <EventPools modelEvent={event} sectionId={model.sectionId} cacheKey={poolsKey.toString()} />
                    )}
                    <Select
                      name="shopId"
                      options={orderBy(shops, 'name').map((e) => ({ value: e.id, label: e.name }))}
                      label="Shop"
                      loading={shopsLoading}
                      required
                      allowEmpty
                    />
                    <Spacer size="none" />
                    {/* Select Invoice and Delivery */}
                    <Label title={`${t.model.invoice._name} / ${t.model.delivery._name}`} />
                    <Grid>
                      {displayDeliverySelect(model) && (
                        <Select
                          style={{ marginBottom: 'var(--spacing-md)' }}
                          name="deliveryId"
                          label={t.model.delivery._name}
                          required
                          options={deliveryOptions}
                        />
                      )}
                      <Select
                        style={{ marginBottom: 'var(--spacing-md)' }}
                        name="invoiceId"
                        label={t.model.invoice._name}
                        required
                        options={invoiceOptions}
                      />
                    </Grid>
                  </>
                )}
              </>
            )}
          </Stack>
        </Form>
      </ScrollWrapper>
      <AddItemsFormFooter
        loading={loading}
        onClose={onClose}
        createAnother={createAnother}
        onChangeCreateAnother={() => setCreateAnother(!createAnother)}
        handleSubmit={handleSubmit}
        canSubmit={canSubmit(model)}
      ></AddItemsFormFooter>
    </>
  );
}
